import { useCallback, useMemo, useRef, useState } from 'react';
import { FORMS_WIDGET_MESSAGES } from '../../constants/reentry-planning.constants';
import { findFormsWidgetFormIds } from './formsWidgetData.util';
import {
  appendFormIds,
  hasSameFormIds,
  removeFormIdFrom,
  toSelectedFormIds,
} from './formsWidgetConfig.util';
import { persistFormsWidgetFormIds } from './formsWidgetPersistence.util';

/**
 * Add Form / Remove persistence for Forms widgets. Every form lives on the one targeted widget:
 * this hook never creates widgets (a new empty Forms widget only comes from the section-level
 * Add Widget action).
 *
 * - Add Form opens the catalog modal for one widget (forcing a fresh catalog). Confirming closes
 *   the modal and shows every selected form immediately, then PATCHes that widget once with its
 *   existing form ids followed by the selected ones (deduped, in order).
 * - Remove PATCHes the widget without that form id (`form_id: null` when it was the last one); the
 *   widget stays.
 *
 * Both are optimistic: on failure the widget rolls back to its exact previous ids (unless a newer
 * change already replaced the optimistic ids) and a red error toast is shown.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: Function,
 *   onOpenCatalog: () => void,
 * }} params
 */
export const useFormsWidgetMutations = ({ casePlanId, sections, setSections, onOpenCatalog }) => {
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const [targetWidgetId, setTargetWidgetId] = useState(null);

  const onAddForm = useCallback(
    (widgetId) => {
      setTargetWidgetId(widgetId);
      onOpenCatalog();
    },
    [onOpenCatalog],
  );

  const closeAddForms = useCallback(() => setTargetWidgetId(null), []);

  const confirmAddForms = useCallback(
    async (selectedForms) => {
      const widgetId = targetWidgetId;
      setTargetWidgetId(null);

      const existingFormIds = findFormsWidgetFormIds(sectionsRef.current, widgetId);
      const nextFormIds = appendFormIds(existingFormIds, toSelectedFormIds(selectedForms));
      if (!casePlanId || !widgetId || hasSameFormIds(existingFormIds, nextFormIds)) {
        return;
      }

      await persistFormsWidgetFormIds({
        casePlanId,
        widgetId,
        previousFormIds: existingFormIds,
        nextFormIds,
        setSections,
        errorMessage: FORMS_WIDGET_MESSAGES.ADD_FAILED,
      });
    },
    [casePlanId, targetWidgetId, setSections],
  );

  const onRemoveForm = useCallback(
    async (widgetId, formId) => {
      if (!casePlanId || !widgetId || !formId) {
        return;
      }

      const previousFormIds = findFormsWidgetFormIds(sectionsRef.current, widgetId);
      const nextFormIds = removeFormIdFrom(previousFormIds, formId);
      await persistFormsWidgetFormIds({
        casePlanId,
        widgetId,
        previousFormIds,
        nextFormIds,
        setSections,
        errorMessage: FORMS_WIDGET_MESSAGES.REMOVE_FAILED,
      });
    },
    [casePlanId, setSections],
  );

  const addFormsModal = useMemo(
    () => ({ open: targetWidgetId !== null, onClose: closeAddForms, onConfirm: confirmAddForms }),
    [targetWidgetId, closeAddForms, confirmAddForms],
  );

  return useMemo(
    () => ({ onAddForm, onRemoveForm, addFormsModal, targetWidgetId }),
    [onAddForm, onRemoveForm, addFormsModal, targetWidgetId],
  );
};
